/**
 * PastLivesResult — standalone viewer for a purchased Past Lives run at
 * /me/past-lives/:id. Lets "My Rites" reopen the cards instead of the buy page.
 * Reuses the usePastLivesRun polling hook; renders a simple vertical card list.
 */
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MeSubpageShell } from '../../components/chrome/MeSubpageShell';
import { usePastLivesRun, type PastLivesCard } from '../../hooks/usePastLivesRun';
import { AiGeneratedLabel } from '../../components/AiGeneratedLabel';
import { ReportMarkdown } from '../../components/ReportMarkdown';

function Card({ card }: { card: PastLivesCard }): JSX.Element {
  const subtitle = [card.profession, card.region, card.era].filter(Boolean).join(' · ');
  return (
    <article style={{ marginBottom: 28 }} data-testid="past-life-card">
      <img
        src={card.portraitUrl}
        alt={card.era}
        loading="lazy"
        style={{ width: '100%', borderRadius: 14, display: 'block', marginBottom: 10 }}
      />
      <div style={{ fontWeight: 600, fontSize: '1.05rem', marginBottom: 2 }}>
        {card.layerDisplayRu || card.era}
      </div>
      {subtitle && (
        <div style={{ opacity: 0.7, fontSize: '0.9rem', marginBottom: 8 }}>{subtitle}</div>
      )}
      <ReportMarkdown markdown={card.narrative} />
    </article>
  );
}

export function PastLivesResult(): JSX.Element {
  const { id = '' } = useParams<{ id: string }>();
  const { t } = useTranslation('common');
  const { snapshot, error } = usePastLivesRun(id);

  // Guard a malformed deep link — usePastLivesRun('') no-ops and would spin forever.
  if (!id) {
    return (
      <MeSubpageShell title={t('results.pastLivesTitle')} data-testid="past-lives-result-page">
        <p role="alert">{t('results.invalidLink')}</p>
      </MeSubpageShell>
    );
  }

  const cards = [...(snapshot?.cards ?? [])].sort((a, b) => a.deliveryIndex - b.deliveryIndex);
  const status = snapshot?.run.status;
  const stillWorking = !!snapshot && status !== 'READY' && status !== 'FAILED' && status !== 'REFUNDED';
  const interrupted = (status === 'FAILED' || status === 'REFUNDED') && cards.length > 0;

  return (
    <MeSubpageShell title={t('results.pastLivesTitle')} data-testid="past-lives-result-page">
      {error && <p role="alert">{t('errors.load')}</p>}
      {!error && !snapshot && <p>{t('states.loading')}</p>}
      {!error && (status === 'FAILED' || status === 'REFUNDED') && cards.length === 0 && (
        <p role="alert">{t('results.couldNotGenerate')}</p>
      )}
      {!error && snapshot && (
        <>
          {stillWorking && <p>{t('results.pastLivesPending')}</p>}
          {interrupted && <p role="alert">{t('results.pastLivesInterrupted')}</p>}
          {cards.length > 0 && <AiGeneratedLabel />}
          {cards.map((card) => (
            <Card key={card.id} card={card} />
          ))}
        </>
      )}
    </MeSubpageShell>
  );
}
